import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import {
  Loading,
  Grid,
  Box,
  Stack,
  Heading,
  Text,
  Button,
  Input,
  SimpleModal,
  ConfirmButton,
  EmptyBox,
  ReadOnlyCopyInput
} from '@/components/lib'
import api from '../../api'
import useStore from '../../store'
import utils from '../../utils/utils'

export default function AccountSettings () {
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyValidity, setNewKeyValidity] = useState('')
  const [tokens, setTokens] = useState([])
  const [newToken, setNewToken] = useState()
  const [loadingTokens, setLoadingTokens] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')
  const navigate = useNavigate()
  const { team, completeLogout } = useStore()

  useEffect(() => {
    setLoadingTokens(true)
    api.accounts.getApiTokens(({ tokens }) => {
      setTokens(tokens)
      setLoadingTokens(false)
    })
  }, [])

  const createApiToken = () => {
    api.accounts.createApiToken(
      { serviceName: newKeyName, validity: parseInt(newKeyValidity) || 0 },
      (token) => {
        utils.toast(
          'success',
          'Token created',
          "We've created that access key for you."
        )
        tokens.push(token)
        setNewKeyName('')
        setNewKeyValidity('')
        setTokens(tokens)
        setNewToken(token)
      },
      (err) => {
        utils.toast('error', 'Unable to create your token', err.message)
      }
    )
  }

  const deleteApiToken = (token) => {
    api.accounts.deleteApiToken(
      token.id,
      () => {
        utils.toast(
          'success',
          'Token deleted',
          "We've deleted that access key for you."
        )
        setTokens(tokens.filter((t) => t.id !== token.id))
      },
      (err) => {
        utils.toast('error', 'Unable to delete your token', err.message)
      }
    )
  }

  const deleteAccount = () => {
    const confirm = window.confirm('Really delete your account?')
    if (!confirm) return
    api.accounts.delete(
      deletePassword,
      () => {
        api.accounts.localLogout(() => completeLogout())
        navigate('/')
        utils.toast('info', 'Account deleted', 'Sorry to see you go.')
      },
      (err) => utils.toast('error', 'Unable to delete account', err.message)
    )
  }

  return (
    <Stack spacing={5}>
      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>
            API keys
          </Heading>
          <Text>
            Grant other applications access to your Trialflare account using API
            keys. Please note that these keys will grant an application the same
            level of permission as your account.
          </Text>
        </Box>

        <Stack rounded='lg' borderWidth='1px' p={3} gap={3}>
          <Heading as='h4' size='sm'>
            Generate a new API key
          </Heading>
          <Box display={{ base: 'block', md: 'flex' }}>
            <Box flex={1} mr={2}>
              <Text fontSize='sm'>
                <strong>Enter a name for your key.</strong> For example, this
                could be the name of the service that will use this key.
              </Text>
            </Box>
            <Input
              w='200px'
              placeholder='Key name'
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
            />
          </Box>

          <Box display={{ base: 'block', md: 'flex' }}>
            <Box flex={1} mr={2}>
              <Text fontSize='sm'>
                <strong>
                  Enter the number of days (from now) that the key should be
                  valid for.
                </strong>{' '}
                Security best practices recommend rotating keys more frequently.
                You can leave this value blank if you don't want the key to ever
                expire.
              </Text>
            </Box>
            <Input
              w='200px'
              placeholder='365'
              value={newKeyValidity}
              onChange={(e) =>
                setNewKeyValidity(e.target.value.replace(/\D/, ''))}
            />
          </Box>

          <Stack direction='row' justifyContent='end'>
            <Button colorPalette='primary' size='sm' onClick={createApiToken}>
              Create key
            </Button>
          </Stack>
        </Stack>

        <Heading as='h4' size='sm'>
          Existing keys
        </Heading>
        <Text>
          The following keys are currently active and have access to your
          Trialflare account.
        </Text>
        {loadingTokens && <Loading />}
        {!tokens || tokens.length === 0
          ? (
            <EmptyBox
              icon='keys'
              title='API keys will appear here once created'
              description='No API keys have been created yet.'
            />
            )
          : (
            <Grid
              templateColumns={{ md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
              gap={2}
            >
              {tokens.map((token) => (
                <Stack
                  key={token}
                  borderWidth='1px'
                  rounded='md'
                  width='100%'
                  p={2}
                >
                  <Heading as='h5' size='sm'>
                    {token.serviceName}
                  </Heading>
                  {token.expiresAt
                    ? (
                      <Text
                        fontSize='xs'
                        color={
                      new Date(token.expiresAt) < new Date() ? 'red' : 'green'
                    }
                      >
                        Expires: {utils.formatDateTime(token.expiresAt)}
                      </Text>
                      )
                    : (
                      <Text fontSize='xs'>No expiry</Text>
                      )}
                  <ConfirmButton
                    trigger={
                      <Button size='xs' variant='outline' colorPalette='red'>
                        Delete
                      </Button>
                  }
                    header='Really delete this API token?'
                    footer='Other services using this token will lose access to your Trialflare account.'
                    onConfirm={(e) => deleteApiToken(token)}
                    actionColor='red'
                  />
                </Stack>
              ))}
            </Grid>
            )}
      </Stack>

      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>
            Data protection complaints
          </Heading>
          <Text>
            Trialflare has a dedicated procedure for managing data protection
            complaints.
          </Text>
        </Box>
        <Box>
          <Button
            as='a'
            target='_blank'
            href={`${import.meta.env.VITE_HELP_CENTRE_HOST}/data_protection_security/#data-protection-complaints`}
          >
            Make a complaint
          </Button>
        </Box>
      </Stack>

      <Stack gap={3} rounded='lg' borderWidth='1px' p={3}>
        <Box>
          <Heading as='h3' size='md'>
            Delete account
          </Heading>
          <Text>
            This will immediately disassociate your user account from{' '}
            {team && team.name}'s team account and will then fully delete your
            user account. {team && team.name}'s team account and any trials
            within it will not be affected. This action cannot be un-done.
          </Text>
        </Box>
        <Text fontSize='lg'>
          To continue, please enter your Trialflare account password. (Note: if
          you only login via single sign-on, you can leave your password blank).
        </Text>

        <Input
          placeholder='Your account password'
          type='password'
          value={deletePassword}
          onChange={(e) => setDeletePassword(e.target.value)}
        />
        <Stack direction='row' justifyContent='end'>
          <Button colorPalette='red' onClick={deleteAccount}>
            Delete account
          </Button>
        </Stack>
      </Stack>

      <SimpleModal
        isOpen={newToken}
        onClose={() => setNewToken()}
        header='Your new Trialflare API key'
        body={
          <Stack>
            <Text>Your new key is displayed below.</Text>
            <Text fontWeight='bold'>
              Important: This is the only time it will be shown to you so please
              make sure you have securely stored it before closing this message.
            </Text>
            <ReadOnlyCopyInput value={newToken?.token} />
          </Stack>
        }
        footer={
          <Button colorPalette='primary' onClick={() => setNewToken()}>
            Close message
          </Button>
        }
      />
    </Stack>
  )
}
